import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ttApi } from "../../lib/ttApi";
import {
  ArrowLeft, Plus, Trash2, Loader2, Database, GitCompareArrows,
  RefreshCcw, PlayCircle, ShieldAlert, CheckCircle2, XCircle,
} from "lucide-react";

// ─── Small helpers (duplicated locally so this file is self-contained) ──
const Tile = ({ label, value, sub, tone }) => {
  const toneClass = { green: "text-emerald-700", amber: "text-amber-700", red: "text-red-700" }[tone] || "text-zinc-900";
  return (
    <div className="bg-white border border-zinc-200 rounded-md p-4">
      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`font-display text-3xl font-extrabold tracking-tight mt-1 ${toneClass}`}>{value}</div>
      <div className="text-xs text-zinc-500 mt-1">{sub}</div>
    </div>
  );
};
const L = ({ label, children }) => (
  <label className="block">
    <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">{label}</div>
    {children}
  </label>
);
const F = ({ label, v, onChange, testid, type = "text" }) => (
  <L label={label}>
    <input type={type} value={v ?? ""} onChange={e => onChange(e.target.value)} className="fld py-2 px-3 w-full" data-testid={testid}/>
  </L>
);
const Err = ({ children }) => <div className="p-2 bg-red-50 border-l-4 border-red-500 text-sm text-red-800">{children}</div>;
const SmallModal = ({ title, onClose, children, testid }) => (
  <div className="fixed inset-0 z-[80] grid place-items-center px-4" style={{ background: "rgba(15,23,42,0.55)" }} onClick={onClose} data-testid={testid}>
    <div className="bg-white rounded-md shadow-2xl max-w-lg w-full p-5 max-h-[88vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
      <div className="font-display text-lg font-extrabold tracking-tight mb-3">{title}</div>
      {children}
    </div>
  </div>
);
const Actions = ({ onClose, busy, testid, label = "Save" }) => (
  <div className="flex items-center justify-end gap-2 pt-2">
    <button type="button" onClick={onClose} className="btn btn-ghost btn-sm">Cancel</button>
    <button type="submit" disabled={busy} className="btn btn-primary btn-sm" data-testid={testid}>{busy ? "Saving…" : label}</button>
  </div>
);
const BackLink = () => <Link to="/test-tech/home" className="text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1"><ArrowLeft size={13}/> Control Tower</Link>;

// ─── Test Data Management Center ─────────────────────────────────────────
const TDC_CATEGORIES = ["Synthetic", "Masked Prod", "Migrated", "Reference"];
const TDC_PII = ["None", "Masked", "Unmasked (restricted)"];

function freshnessPill(status) {
  const map = {
    "Fresh":           ["pill-green", CheckCircle2],
    "Stale":           ["bg-amber-50 text-amber-800 border border-amber-200", ShieldAlert],
    "Expired":         ["bg-red-50 text-red-700 border border-red-200", XCircle],
    "Never refreshed": ["pill-neutral", ShieldAlert],
    "Unknown":         ["pill-neutral", ShieldAlert],
  };
  const [cls, Icon] = map[status] || map["Unknown"];
  return <span className={`pill ${cls} inline-flex items-center gap-1 text-[10px]`}><Icon size={11}/> {status}</span>;
}

export function TestDataCenter() {
  const { pid } = useParams();
  const [rows, setRows] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [open, setOpen] = useState(null); // { set } or {} for new
  const [busy, setBusy] = useState(true);
  const [refreshingId, setRefreshingId] = useState(null);
  const refresh = async () => {
    setBusy(true);
    try {
      const [r, k] = await Promise.all([ttApi.listTdc(pid), ttApi.tdcKpis(pid)]);
      setRows(r.items || []); setKpis(k);
    } finally { setBusy(false); }
  };
  useEffect(() => { refresh(); }, [pid]);
  const del = async (tid) => { if (window.confirm("Delete test data set?")) { await ttApi.deleteTdc(pid, tid); refresh(); } };
  const doRefresh = async (tid) => {
    setRefreshingId(tid);
    try { await ttApi.refreshTdc(pid, tid); await refresh(); }
    finally { setRefreshingId(null); }
  };
  return (
    <main className="max-w-6xl mx-auto px-6 py-8" data-testid="tt-testdata">
      <BackLink/>
      <div className="flex items-center justify-between mt-2 mb-5">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 inline-flex items-center gap-2"><Database size={11}/> Test Data Management</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Test Data Center</h1>
          <p className="text-sm text-zinc-600 mt-1">Track synthetic, masked and migrated test data · PII posture · freshness SLAs</p>
        </div>
        <button onClick={() => setOpen({})} className="btn btn-primary btn-sm inline-flex items-center gap-1" data-testid="tt-new-tdc"><Plus size={13}/> New data set</button>
      </div>

      {kpis && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="tt-tdc-kpis">
          <Tile label="Total sets" value={kpis.total} sub={`${kpis.total_records.toLocaleString()} records`}/>
          <Tile label="Fresh" value={kpis.fresh} sub="within SLA" tone="green"/>
          <Tile label="Stale" value={kpis.stale} sub="approaching expiry" tone={kpis.stale > 0 ? "amber" : "green"}/>
          <Tile label="Expired" value={kpis.expired} sub="refresh overdue" tone={kpis.expired > 0 ? "red" : "green"}/>
          <Tile label="Unmasked PII" value={kpis.unmasked_pii} sub="restricted access" tone={kpis.unmasked_pii > 0 ? "red" : "green"}/>
        </div>
      )}

      {busy ? (
        <div className="text-sm text-zinc-500"><Loader2 className="inline animate-spin" size={13}/> Loading…</div>
      ) : rows.length === 0 ? (
        <div className="p-6 bg-white border border-dashed rounded-sm text-center text-sm text-zinc-500">
          No test data sets yet. Click <b>New data set</b> to begin — try "US Customer Master · Synthetic", "EU Open Orders · Masked".
        </div>
      ) : (
        <div className="bg-white border border-zinc-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-xs font-mono uppercase tracking-widest text-zinc-500">
              <tr>
                <th className="text-left px-4 py-2">Name</th>
                <th className="text-left px-4 py-2">Category</th>
                <th className="text-left px-4 py-2">PII</th>
                <th className="text-left px-4 py-2">Env</th>
                <th className="text-right px-4 py-2">Records</th>
                <th className="text-left px-4 py-2">Freshness</th>
                <th className="text-left px-4 py-2">Last refresh</th>
                <th className="text-right px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r._id} className="border-t border-zinc-100 hover:bg-zinc-50" data-testid={`tt-tdc-${r._id}`}>
                  <td className="px-4 py-2">
                    <div className="font-semibold text-zinc-900">{r.name}</div>
                    {r.coverage_tags?.length > 0 && (
                      <div className="text-[10px] text-zinc-500 mt-0.5">{r.coverage_tags.join(" · ")}</div>
                    )}
                  </td>
                  <td className="px-4 py-2 text-xs">{r.category}</td>
                  <td className="px-4 py-2 text-xs">
                    <span className={`pill text-[10px] ${r.pii_status === "Unmasked (restricted)" ? "bg-red-50 text-red-700 border border-red-200" : r.pii_status === "Masked" ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "pill-neutral"}`}>
                      {r.pii_status}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-xs font-mono text-zinc-600">{r.environment || "—"}</td>
                  <td className="px-4 py-2 text-right font-mono text-xs">{(r.record_count || 0).toLocaleString()}</td>
                  <td className="px-4 py-2">
                    {freshnessPill(r.freshness?.status)}
                    {r.freshness?.days_old != null && <div className="text-[10px] text-zinc-500 mt-0.5 font-mono">{r.freshness.days_old}d old · SLA {r.freshness_days}d</div>}
                  </td>
                  <td className="px-4 py-2 text-xs text-zinc-500 font-mono">{r.last_refresh ? new Date(r.last_refresh).toLocaleDateString() : "—"}</td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    <button
                      onClick={() => doRefresh(r._id)}
                      disabled={refreshingId === r._id}
                      className="text-xs text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1"
                      data-testid={`tt-tdc-refresh-${r._id}`}
                    >
                      {refreshingId === r._id ? <Loader2 className="animate-spin" size={12}/> : <RefreshCcw size={12}/>} Refresh
                    </button>
                    <button onClick={() => setOpen({ set: r })} className="ml-3 text-xs text-zinc-700 hover:text-zinc-900 underline underline-offset-2" data-testid={`tt-tdc-edit-${r._id}`}>Edit</button>
                    <button onClick={() => del(r._id)} className="ml-3 text-xs text-red-700"><Trash2 size={11}/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {open && <TDCModal pid={pid} initial={open.set} onClose={() => setOpen(null)} onDone={() => { setOpen(null); refresh(); }}/>}
    </main>
  );
}

function TDCModal({ pid, initial, onClose, onDone }) {
  const [f, setF] = useState({
    name: initial?.name || "",
    category: initial?.category || "Synthetic",
    pii_status: initial?.pii_status || "None",
    owner: initial?.owner || "",
    environment: initial?.environment || "",
    record_count: initial?.record_count ?? 0,
    freshness_days: initial?.freshness_days ?? 14,
    last_refresh: initial?.last_refresh ? initial.last_refresh.slice(0, 10) : "",
    coverage_tags: (initial?.coverage_tags || []).join(", "),
    notes: initial?.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const body = {
        ...f,
        record_count: Number(f.record_count) || 0,
        freshness_days: Number(f.freshness_days) || 0,
        last_refresh: f.last_refresh ? new Date(f.last_refresh).toISOString() : undefined,
        coverage_tags: f.coverage_tags.split(",").map(s => s.trim()).filter(Boolean),
      };
      if (initial?._id) await ttApi.updateTdc(pid, initial._id, body);
      else await ttApi.createTdc(pid, body);
      onDone();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  return (
    <SmallModal title={initial?._id ? "Edit Data Set" : "New Data Set"} onClose={onClose} testid="tt-tdc-modal">
      <form onSubmit={submit} className="space-y-3">
        <F label="Name" v={f.name} onChange={v => setF({...f, name: v})} testid="tt-tdc-name"/>
        <div className="grid grid-cols-2 gap-3">
          <L label="Category">
            <select value={f.category} onChange={e => setF({...f, category: e.target.value})} className="fld py-2 px-3 w-full" data-testid="tt-tdc-category">
              {TDC_CATEGORIES.map(x => <option key={x}>{x}</option>)}
            </select>
          </L>
          <L label="PII posture">
            <select value={f.pii_status} onChange={e => setF({...f, pii_status: e.target.value})} className="fld py-2 px-3 w-full" data-testid="tt-tdc-pii">
              {TDC_PII.map(x => <option key={x}>{x}</option>)}
            </select>
          </L>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <F label="Environment" v={f.environment} onChange={v => setF({...f, environment: v})} testid="tt-tdc-env"/>
          <F label="Owner" v={f.owner} onChange={v => setF({...f, owner: v})} testid="tt-tdc-owner"/>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <F label="Record count" v={f.record_count} onChange={v => setF({...f, record_count: v})} testid="tt-tdc-count" type="number"/>
          <F label="Freshness SLA (days)" v={f.freshness_days} onChange={v => setF({...f, freshness_days: v})} testid="tt-tdc-sla" type="number"/>
        </div>
        <F label="Last refresh (YYYY-MM-DD)" v={f.last_refresh} onChange={v => setF({...f, last_refresh: v})} testid="tt-tdc-last" type="date"/>
        <F label="Coverage tags (comma-separated)" v={f.coverage_tags} onChange={v => setF({...f, coverage_tags: v})} testid="tt-tdc-tags"/>
        <F label="Notes" v={f.notes} onChange={v => setF({...f, notes: v})} testid="tt-tdc-notes"/>
        {err && <Err>{err}</Err>}
        <Actions onClose={onClose} busy={busy} testid="tt-tdc-submit"/>
      </form>
    </SmallModal>
  );
}

// ─── System Integration Testing Center ───────────────────────────────────
const SIT_STATUSES = ["Pending", "In Progress", "Pass", "Partial", "Fail", "Blocked"];

function sitStatusPill(status) {
  const map = {
    "Pass":        ["pill-green", CheckCircle2],
    "Partial":     ["bg-amber-50 text-amber-800 border border-amber-200", ShieldAlert],
    "Fail":        ["bg-red-50 text-red-700 border border-red-200", XCircle],
    "Blocked":     ["bg-red-50 text-red-700 border border-red-200", XCircle],
    "In Progress": ["bg-blue-50 text-blue-700 border border-blue-200", PlayCircle],
    "Pending":     ["pill-neutral", PlayCircle],
  };
  const [cls, Icon] = map[status] || map["Pending"];
  return <span className={`pill ${cls} inline-flex items-center gap-1 text-[10px]`}><Icon size={11}/> {status}</span>;
}

export function SITCenter() {
  const { pid } = useParams();
  const [rows, setRows] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [open, setOpen] = useState(null);
  const [runFor, setRunFor] = useState(null);
  const [busy, setBusy] = useState(true);
  const refresh = async () => {
    setBusy(true);
    try {
      const [r, k] = await Promise.all([ttApi.listSit(pid), ttApi.sitKpis(pid)]);
      setRows(r.items || []); setKpis(k);
    } finally { setBusy(false); }
  };
  useEffect(() => { refresh(); }, [pid]);
  const del = async (sid) => { if (window.confirm("Delete SIT flow?")) { await ttApi.deleteSit(pid, sid); refresh(); } };

  return (
    <main className="max-w-6xl mx-auto px-6 py-8" data-testid="tt-sit">
      <BackLink/>
      <div className="flex items-center justify-between mt-2 mb-5">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 inline-flex items-center gap-2"><GitCompareArrows size={11}/> System Integration Testing</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">SIT Command Center</h1>
          <p className="text-sm text-zinc-600 mt-1">Source → Middleware → Target data flows · record-level reconciliation</p>
        </div>
        <button onClick={() => setOpen({})} className="btn btn-primary btn-sm inline-flex items-center gap-1" data-testid="tt-new-sit"><Plus size={13}/> New flow</button>
      </div>

      {kpis && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="tt-sit-kpis">
          <Tile label="Flows" value={kpis.total} sub={`${kpis.by_status.Pass} passed`}/>
          <Tile label="Pass" value={kpis.by_status.Pass} sub="green" tone="green"/>
          <Tile label="Partial / Fail" value={kpis.by_status.Partial + kpis.by_status.Fail} sub="investigate" tone={(kpis.by_status.Partial + kpis.by_status.Fail) > 0 ? "red" : "green"}/>
          <Tile label="Overall recon" value={`${kpis.overall_reconciliation_pct}%`} sub={`${kpis.total_matched.toLocaleString()} / ${kpis.total_sent.toLocaleString()}`} tone={kpis.overall_reconciliation_pct >= 99 ? "green" : kpis.overall_reconciliation_pct >= 90 ? "amber" : "red"}/>
          <Tile label="Mismatch flows" value={kpis.mismatch_flows} sub="sent ≠ matched+rej" tone={kpis.mismatch_flows > 0 ? "red" : "green"}/>
        </div>
      )}

      {busy ? (
        <div className="text-sm text-zinc-500"><Loader2 className="inline animate-spin" size={13}/> Loading…</div>
      ) : rows.length === 0 ? (
        <div className="p-6 bg-white border border-dashed rounded-sm text-center text-sm text-zinc-500">
          No SIT flows configured. Click <b>New flow</b> to begin — try "SIT-OTC-001 · Salesforce → MuleSoft → S/4HANA (Orders)".
        </div>
      ) : (
        <div className="bg-white border border-zinc-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-xs font-mono uppercase tracking-widest text-zinc-500">
              <tr>
                <th className="text-left px-4 py-2">Code</th>
                <th className="text-left px-4 py-2">Flow</th>
                <th className="text-left px-4 py-2">Path</th>
                <th className="text-left px-4 py-2">Type</th>
                <th className="text-right px-4 py-2">Sent</th>
                <th className="text-right px-4 py-2">Matched</th>
                <th className="text-left px-4 py-2">Recon</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-right px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r._id} className="border-t border-zinc-100 hover:bg-zinc-50 align-top" data-testid={`tt-sit-${r._id}`}>
                  <td className="px-4 py-2 font-mono text-xs text-zinc-700">{r.code}</td>
                  <td className="px-4 py-2">
                    <div className="font-semibold text-zinc-900">{r.name}</div>
                    {r.owner && <div className="text-[10px] text-zinc-500 mt-0.5">owner · {r.owner}</div>}
                  </td>
                  <td className="px-4 py-2 text-[11px] font-mono text-zinc-600">
                    {r.source_system} <span className="text-zinc-400">→</span> {r.middleware || "—"} <span className="text-zinc-400">→</span> {r.target_system}
                  </td>
                  <td className="px-4 py-2 text-xs">
                    <div>{r.message_type}</div>
                    <div className="text-[10px] text-zinc-500">{r.direction}</div>
                  </td>
                  <td className="px-4 py-2 text-right font-mono text-xs">{(r.records_sent || 0).toLocaleString()}</td>
                  <td className="px-4 py-2 text-right font-mono text-xs">{(r.records_matched || 0).toLocaleString()}</td>
                  <td className="px-4 py-2">
                    <div className={`font-display text-lg font-extrabold ${r.recon?.rag === "green" ? "text-emerald-700" : r.recon?.rag === "amber" ? "text-amber-700" : r.recon?.rag === "red" ? "text-red-700" : "text-zinc-500"}`}>
                      {r.recon?.reconciliation_pct ?? 0}%
                    </div>
                    {(r.recon?.mismatch || 0) > 0 && (
                      <div className="text-[10px] text-red-700 font-mono">{r.recon.mismatch} mismatch</div>
                    )}
                  </td>
                  <td className="px-4 py-2">{sitStatusPill(r.status)}</td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    <button onClick={() => setRunFor(r)} className="text-xs text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1" data-testid={`tt-sit-run-${r._id}`}><PlayCircle size={12}/> Run</button>
                    <button onClick={() => setOpen({ flow: r })} className="ml-3 text-xs text-zinc-700 hover:text-zinc-900 underline underline-offset-2" data-testid={`tt-sit-edit-${r._id}`}>Edit</button>
                    <button onClick={() => del(r._id)} className="ml-3 text-xs text-red-700"><Trash2 size={11}/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {open && <SITModal pid={pid} initial={open.flow} onClose={() => setOpen(null)} onDone={() => { setOpen(null); refresh(); }}/>}
      {runFor && <SITRunModal pid={pid} flow={runFor} onClose={() => setRunFor(null)} onDone={() => { setRunFor(null); refresh(); }}/>}
    </main>
  );
}

function SITModal({ pid, initial, onClose, onDone }) {
  const [f, setF] = useState({
    code: initial?.code || "",
    name: initial?.name || "",
    source_system: initial?.source_system || "",
    middleware: initial?.middleware || "",
    target_system: initial?.target_system || "",
    direction: initial?.direction || "outbound",
    message_type: initial?.message_type || "real-time",
    volume_expected: initial?.volume_expected ?? 0,
    owner: initial?.owner || "",
    notes: initial?.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const body = {
        ...f,
        volume_expected: Number(f.volume_expected) || 0,
      };
      if (initial?._id) await ttApi.updateSit(pid, initial._id, body);
      else await ttApi.createSit(pid, body);
      onDone();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  return (
    <SmallModal title={initial?._id ? "Edit SIT Flow" : "New SIT Flow"} onClose={onClose} testid="tt-sit-modal">
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <F label="Code (e.g. SIT-OTC-001)" v={f.code} onChange={v => setF({...f, code: v})} testid="tt-sit-code"/>
          <F label="Owner" v={f.owner} onChange={v => setF({...f, owner: v})} testid="tt-sit-owner"/>
        </div>
        <F label="Name" v={f.name} onChange={v => setF({...f, name: v})} testid="tt-sit-name"/>
        <div className="grid grid-cols-3 gap-3">
          <F label="Source system" v={f.source_system} onChange={v => setF({...f, source_system: v})} testid="tt-sit-source"/>
          <F label="Middleware" v={f.middleware} onChange={v => setF({...f, middleware: v})} testid="tt-sit-mw"/>
          <F label="Target system" v={f.target_system} onChange={v => setF({...f, target_system: v})} testid="tt-sit-target"/>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <L label="Direction">
            <select value={f.direction} onChange={e => setF({...f, direction: e.target.value})} className="fld py-2 px-3 w-full" data-testid="tt-sit-direction">
              {["inbound","outbound","bidirectional"].map(x => <option key={x}>{x}</option>)}
            </select>
          </L>
          <L label="Message type">
            <select value={f.message_type} onChange={e => setF({...f, message_type: e.target.value})} className="fld py-2 px-3 w-full" data-testid="tt-sit-mtype">
              {["batch","real-time","near-real-time","event"].map(x => <option key={x}>{x}</option>)}
            </select>
          </L>
        </div>
        <F label="Expected volume (records)" v={f.volume_expected} onChange={v => setF({...f, volume_expected: v})} testid="tt-sit-volume" type="number"/>
        <F label="Notes" v={f.notes} onChange={v => setF({...f, notes: v})} testid="tt-sit-notes"/>
        {err && <Err>{err}</Err>}
        <Actions onClose={onClose} busy={busy} testid="tt-sit-submit"/>
      </form>
    </SmallModal>
  );
}

function SITRunModal({ pid, flow, onClose, onDone }) {
  const [f, setF] = useState({
    records_sent: flow.records_sent ?? 0,
    records_received: flow.records_received ?? 0,
    records_matched: flow.records_matched ?? 0,
    records_rejected: flow.records_rejected ?? 0,
    status: flow.status || "In Progress",
    evidence: flow.evidence || "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const sent = Number(f.records_sent) || 0;
  const matched = Number(f.records_matched) || 0;
  const rej = Number(f.records_rejected) || 0;
  const previewRecon = sent ? Math.round((matched / sent) * 1000) / 10 : 0;
  const previewMismatch = Math.max(0, sent - matched - rej);
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      await ttApi.runSit(pid, flow._id, {
        records_sent: Number(f.records_sent) || 0,
        records_received: Number(f.records_received) || 0,
        records_matched: Number(f.records_matched) || 0,
        records_rejected: Number(f.records_rejected) || 0,
        status: f.status,
        evidence: f.evidence,
      });
      onDone();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  return (
    <SmallModal title={`Record run · ${flow.code}`} onClose={onClose} testid="tt-sit-run-modal">
      <form onSubmit={submit} className="space-y-3">
        <div className="p-3 bg-zinc-50 border border-zinc-200 rounded-sm">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Live preview</div>
          <div className="flex items-baseline gap-4 mt-1">
            <div>
              <span className={`font-display text-2xl font-extrabold ${previewRecon >= 99 ? "text-emerald-700" : previewRecon >= 90 ? "text-amber-700" : "text-red-700"}`}>{previewRecon}%</span>
              <span className="text-xs text-zinc-500 ml-1">reconciliation</span>
            </div>
            <div className="text-xs text-zinc-500">
              {previewMismatch > 0 ? <span className="text-red-700 font-mono">{previewMismatch} mismatch</span> : <span className="text-emerald-700">no mismatch</span>}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <F label="Records sent" v={f.records_sent} onChange={v => setF({...f, records_sent: v})} testid="tt-sit-run-sent" type="number"/>
          <F label="Records received" v={f.records_received} onChange={v => setF({...f, records_received: v})} testid="tt-sit-run-recv" type="number"/>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <F label="Records matched" v={f.records_matched} onChange={v => setF({...f, records_matched: v})} testid="tt-sit-run-match" type="number"/>
          <F label="Records rejected" v={f.records_rejected} onChange={v => setF({...f, records_rejected: v})} testid="tt-sit-run-rej" type="number"/>
        </div>
        <L label="Status">
          <select value={f.status} onChange={e => setF({...f, status: e.target.value})} className="fld py-2 px-3 w-full" data-testid="tt-sit-run-status">
            {SIT_STATUSES.map(x => <option key={x}>{x}</option>)}
          </select>
        </L>
        <F label="Evidence (ticket id, log reference, notes)" v={f.evidence} onChange={v => setF({...f, evidence: v})} testid="tt-sit-run-evidence"/>
        {err && <Err>{err}</Err>}
        <Actions onClose={onClose} busy={busy} testid="tt-sit-run-submit" label="Record run"/>
      </form>
    </SmallModal>
  );
}
